import { centsToEuros, eurosToCents } from '@/constants'
import { AmountField } from '@/ui/rapid-ui'
import { Checkbox } from '@headlessui/react'
import {
	type CampaignAddonData,
	campaignsShowQueryKey,
	useCampaignAddonDestroy,
	useCampaignAddonStore,
} from '@jobilla/nexus-sdk'
import ctl from '@netlify/classnames-template-literals'
import { useQueryClient } from '@tanstack/react-query'
import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

export type CampaignAddonProps = {
	campaignId: string
	addon: CampaignAddonData
	readOnly?: boolean
}

export function CampaignAddon({ campaignId, addon, readOnly }: CampaignAddonProps) {
	const queryClient = useQueryClient()
	const { i18n } = useTranslation()

	const [selected, setSelected] = useState(addon.selected)
	const [price, setPrice] = useState(centsToEuros(addon.price ?? 0))
	const hasInteractedRef = useRef(false)

	const campaignAddonMutation = useCampaignAddonStore({
		mutationKey: ['campaign-addon-store', campaignId, addon.id],
		onSuccess: async () => {
			await queryClient.refetchQueries({
				queryKey: [campaignsShowQueryKey],
			})
		},
		onError: () => {
			setSelected(false)
		},
	})

	const campaignAddonMutationRemove = useCampaignAddonDestroy({
		mutationKey: ['campaign-addon-remove', campaignId, addon.id],
		onSuccess: async () => {
			await queryClient.refetchQueries({
				queryKey: [campaignsShowQueryKey],
			})
		},
		onError: () => {
			setSelected(true)
		},
	})

	const handleSelectionChange = (checked: boolean) => {
		setSelected(checked)

		if (checked) {
			campaignAddonMutation.mutate({
				campaign: campaignId,
				addon: addon.id,
				data: { price: eurosToCents(price) },
			})
		} else {
			campaignAddonMutationRemove.mutate({ campaign: campaignId, addon: addon.id })
		}
	}

	const handlePriceBlur = () => {
		if (!selected || !hasInteractedRef.current) { return }

		campaignAddonMutation.mutate({ campaign: campaignId, addon: addon.id, data: { price: eurosToCents(price) } })
	}

	const handlePriceChange = (newPrice: number) => {
		if (!newPrice) {
			setPrice(0)
			return
		}
		hasInteractedRef.current = true
		setPrice(newPrice)
	}

	return (
		<div
			className={ctl(`
                mt-3 bg-white min-h-14
                ring ring-grey-200 rounded-md flex items-center gap-3 px-3 py-3
                text-sm
                transition-colors duration-200
                ${selected ? 'ring-blue-200 bg-blue-50/30' : ''}
            `)}
		>
			<Checkbox
				checked={selected}
				onChange={handleSelectionChange}
				disabled={readOnly}
				className={ctl(`
                    flex items-center gap-3 flex-1 min-w-0
                    ${readOnly ? 'cursor-default opacity-60' : 'cursor-pointer'}
                    focus:outline-none
                `)}
			>
				{({ checked, focus }) => (
					<>
						<div
							className={ctl(`
                                w-5 h-5 rounded border flex items-center justify-center transition-colors shrink-0
                                ${focus ? 'ring-2 ring-blue-500 ring-offset-2' : ''}
                                ${checked ? 'bg-blue-500 border-blue-500' : 'border-grey-300 bg-white'}
                            `)}
						>
							{checked && (
								<svg className="w-3 h-3 text-white" viewBox="0 0 12 12" fill="none">
									<path
										d="M2.5 6L5 8.5L9.5 3.5"
										stroke="currentColor"
										strokeWidth="2"
										strokeLinecap="round"
										strokeLinejoin="round"
									/>
								</svg>
							)}
						</div>
						<span className="text-grey-800">{addon.name}</span>
					</>
				)}
			</Checkbox>

			{selected && (
				<div className="w-44 shrink-0">
					<AmountField
						label="Price"
						hideLabel
						value={price}
						onChange={handlePriceChange}
						onBlur={handlePriceBlur}
						readOnly={readOnly}
						locale={i18n.language}
						prefix="€"
						suffix="+ VAT"
						placeholder="0.00"
					/>
				</div>
			)}
		</div>
	)
}
